import React, {useEffect, useRef, useState} from 'react';
import MessageBroker from '@bonprix/pattern-library/components/message-broker';
import ProductMessages from '@product/components/message-list';
import addToCart from '@core/scripts/components/actions/add-to-cart';
import openIntoCartLayer from '@core/scripts/components/actions/open-into-cart-layer';
import Landmark from '@core/scripts/components/landmark-adapter';
import fetchWithCSRF from '@core/scripts/helper/fetch-with-csrf';
import Url from '@core/scripts/helper/url';
import PointOfSaleId from '@core/scripts/point-of-sale-id';

import FitFinderAdapter from '@product/features/fit-finder/fit-finder-adapter';
import FitFinderAPI from '@product/features/fit-finder/fit-finder-api';
import FitFinderTracking from '@product/features/fit-finder/fit-finder-tracking';
import FitFinderWidget from '@product/features/fit-finder/fit-finder-widget';
import ExternalDialogHistory from '@product/features/history/external-dialog';
import {Typography} from "@bonprix/design-system";

export default function FitFinder({product, texts, sizeTableRef, hideFitFinder}) {

    const [fitFinderData, setFitFinderData] = useState(null);

    const currentStyle = useRef(product.currentStyle);
    const currentItem = useRef(product.currentItem);
    const currentItemOption = useRef(product.currentItemOption);
    const sizeTableLink = useRef(null);
    const fitFinderLinkRef = useRef(null);
    const initializationMade = useRef(false);

    useEffect(() => {
        FitFinderAPI.get().then((fitFinderData) => {
            setFitFinderData(fitFinderData);
        }).catch(() => {
            FitFinderTracking.disable();
        });
    }, []);

    useEffect(() => {
        if (fitFinderData) {
            initializeFitFinderWidget(product, texts, currentStyle.current, currentItem.current);
            MessageBroker.subscribe(ProductMessages.CHANGED, ({style, item, itemOption}) => {
                if (style) {
                    currentStyle.current = style;
                }
                if (item) {
                    currentItem.current = item;
                }
                if (itemOption) {
                    currentItemOption.current = itemOption;
                }
                initializeFitFinderWidget(product, texts, currentStyle.current, currentItem.current);
            });
        }
    }, [fitFinderData]);

    useEffect(() => {
        if (sizeTableRef.current) {
            sizeTableLink.current = sizeTableRef.current.querySelector('a');
        }
    }, [sizeTableRef]);

    function initializeFitFinderWidget(product, texts, currentStyle, currentItem) {
        setFitAnalyticsData(product, texts, currentStyle, currentItem);

        let sizeTableLinkHtml;
        if (!sizeTableLinkHtml && sizeTableLink.current) {
            sizeTableLinkHtml = sizeTableLink.current.innerHTML;
        }

        // destroy widget and unmount integration when currentItem has orderHint
        if (initializationMade.current && currentItem.orderHint) {
            initializationMade.current = false;
            FitFinderWidget.destroy(fitFinderLinkRef.current);
            FitFinderTracking.disable();
            if (sizeTableLink.current) {
                sizeTableLink.current.innerHTML = sizeTableLinkHtml;
            }
        }

        if (!initializationMade.current && !currentItem.orderHint) {
            initializationMade.current = true;
            const container = '.product__content__with____full_width';
            FitFinderWidget.initialize(texts.ada19_fit_analytics_link, container, hideFitFinder);
        }
        FitFinderTracking.enable(product.shopId, product.styleId, currentItem.id);
        
        ExternalDialogHistory.initById('fit-finder', 'uclw_wrapper', function() {
            const node = document.querySelector('#uclw_close_link');
            if (node) {
                node.click();
            }
        });
    }

    async function performAddToCart(product, texts, productId, size) {
        const sizeName = size.toString().split('-')[0];
        const styleName = size.toString().split('-')[1];

        let item;
        if (styleName) {
            const style = product.styles.find((style) => style.name === styleName);
            item = style.items.find((item) => item.name === currentItem.current.name);
        } else {
            item = currentItem.current;
        }

        const itemOption = item.itemOptions.find((itemOption) => itemOption.name === sizeName);
        const itemOptionId = itemOption?.id;
        if (itemOptionId) {
            const cartSummary = await addToCart(
                product.styleId,
                product.shopId,
                item.id,
                item.itemNumber,
                itemOptionId,
                sizeName,
                product.promotion,
                null,
                'adp',
                itemOption?.price?.unified?.value,
                1,
                PointOfSaleId.FIT_FINDER,
                null,
                null,
                null
            );

            if (cartSummary) {
                openIntoCartLayer(
                    itemOptionId,
                    product.shopId,
                    null,
                    null,
                    PointOfSaleId.FIT_FINDER,
                    null,
                    'adp',
                    product.channelId,
                    null
                );
                Landmark.sendClickLandmark(`adp_fit_addtobasket\\${PointOfSaleId.FIT_FINDER}\\${item.itemNumber}`);
            } else {
                MessageBroker.publish(ProductMessages.SHOW_SUBMIT_ERROR, {error: texts.ada_add_to_cart_error});
            }
        }
    }

    function performSelectSize(product, productId, size) {
        const styleName = size.toString().split('-')[1];
        const itemId = parseInt(productId);
        const itemName = currentStyle.current.items.find((item) => item.id === itemId)?.name;
        const itemOptionName = size.toString().split('-')[0];
        let item;
        let style;
        if (styleName) {
            style = product.styles.find((style) => style.name === styleName);
            item = style.items.find((item) => item.name === itemName);
        } else {
            style = currentStyle.current;
            item = currentItem.current;
        }
        const itemOption = item.itemOptions.find((itemOption) => itemOption.name === itemOptionName);
        MessageBroker.publish(ProductMessages.CHANGED, {style, item, itemOption});
    }

    function setFitAnalyticsData(product, texts, currentStyle, currentItem) {
        const allProductIds = [];
        const sizes = [];
        const currentProductId = currentItem.id;
        const mainThumbnail = currentItem.images
            .filter(image => image.type !== 'VIDEO')[product.selectedImageIndex]?.preview.sources
            .find((source) => source.mediaQuery === '(min-width:1200px)')?.srcSet.src;

        product.styles?.forEach((style) => {
            style.items.forEach((item) => {
                allProductIds.push(item.id);

                if (item.name === currentItem.name) {
                    item.itemOptions.forEach((itemOption) => {
                        sizes.push({
                            value: itemOption.name + (!product.singleStyle ? '-' + style.name : ''),
                            isAvailable: itemOption.availability.available
                        });
                    });
                }
            });
        });

        window.fitAnalyticsData = FitFinderAdapter.create({
            product,
            texts,
            mainThumbnail,
            fitFinderData,
            currentProductId,
            allProductIds,
            sizes,
            performAddToCart,
            performSelectSize,
            resolveCurrentSize,
            resolveCartItems
        });
    }

    async function resolveCartItems() {
        return fetchWithCSRF(Url.toAbsolute('/ajax/list/cart/'), {
            method: 'GET',
            headers: new Headers({
                'Content-Type': 'application/json'
            })
        })
            .then((response) => {
                if (response.ok) {
                    return response.json();
                }
                return null;
            })
            .then((cart) => {
                const cartItems = [];
                cart.items.forEach((item) =>
                    cartItems.push({
                        productId: item.itemId,
                        size: item.itemOptionName + (item.styleName ? '-' + item.styleName : '')
                    })
                );
                return cartItems;
            });
    }

    function resolveCurrentSize(product) {
        if (product.currentItemOption) {
            return product.currentItemOption.id.toString();
        }
        return '';
    }

    if (hideFitFinder) {
        return (
            <div style={{visibility: 'hidden'}}>
                <div id="fitanalytics-size-advisor" ref={fitFinderLinkRef}/>
            </div>
        );
    }

    return (
        <Typography variant="body1" component="div">
            <div id="fitanalytics-size-advisor" ref={fitFinderLinkRef}/>
        </Typography>
    );
}
